import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, ChevronDown, KeyRound, Loader2, Plus, UserPlus, UserX, UserCheck } from "lucide-react";

const inputCls = "w-full rounded-lg border border-emerald-950/15 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600";
const emptyOrg = { legal_name: "", trade_name: "", contact_name: "", contact_email: "", contact_phone: "", plan_tier: "free", seat_limit: 3 };
const emptyUser = { name: "", email: "", designation: "", phone: "", password: "" };

export default function OrgManager({ api }) {
  const [orgs, setOrgs] = useState([]);
  const [upgrades, setUpgrades] = useState([]);
  const [orgDraft, setOrgDraft] = useState(emptyOrg);
  const [expanded, setExpanded] = useState(null);
  const [orgUsers, setOrgUsers] = useState({});
  const [adminDraft, setAdminDraft] = useState(emptyUser);
  const [creatingAdminFor, setCreatingAdminFor] = useState(null);
  const [passwordOnce, setPasswordOnce] = useState(null);
  const [setPwFor, setSetPwFor] = useState(null);
  const [setPwValue, setSetPwValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [o, u] = await Promise.all([api("/admin/orgs"), api("/admin/seat-upgrade-requests")]);
    setOrgs(o.items);
    setUpgrades(u.items);
  }, [api]);

  useEffect(() => {
    load().catch((e) => toast.error(e.message));
  }, [load]);

  const loadUsers = async (orgId) => {
    const data = await api(`/admin/orgs/${orgId}/users`);
    setOrgUsers((m) => ({ ...m, [orgId]: data.items }));
  };

  const toggleExpand = (orgId) => {
    const next = expanded === orgId ? null : orgId;
    setExpanded(next);
    if (next) loadUsers(next).catch((e) => toast.error(e.message));
  };

  const createOrg = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/admin/orgs", { method: "POST", body: JSON.stringify({ ...orgDraft, seat_limit: Number(orgDraft.seat_limit) }) });
      setOrgDraft(emptyOrg);
      await load();
      toast.success("Organisation created");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const patchOrg = async (orgId, patch) => {
    try {
      await api(`/admin/orgs/${orgId}`, { method: "PATCH", body: JSON.stringify(patch) });
      await load();
      toast.success("Organisation updated");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const createClientAdmin = async (orgId) => {
    try {
      await api(`/admin/orgs/${orgId}/client-admin`, { method: "POST", body: JSON.stringify(adminDraft) });
      setPasswordOnce({ password: adminDraft.password, forName: adminDraft.name });
      setAdminDraft(emptyUser);
      setCreatingAdminFor(null);
      await loadUsers(orgId);
      await load();
      toast.success("Client admin created");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const savePassword = async (userId) => {
    try {
      await api(`/admin/users/${userId}/set-password`, { method: "POST", body: JSON.stringify({ password: setPwValue }) });
      const all = Object.values(orgUsers).flat();
      const t = all.find((u) => u.id === userId);
      setPasswordOnce({ password: setPwValue, forName: t ? t.name : "user" });
      setSetPwFor(null);
      setSetPwValue("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const setStatus = async (userId, action, orgId) => {
    try {
      await api(`/admin/users/${userId}/${action}`, { method: "POST" });
      await loadUsers(orgId);
      await load();
      toast.success(action === "disable" ? "User disabled" : "User re-enabled");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <section className="mt-8" data-testid="org-manager">
      <div className="flex items-center gap-2">
        <Building2 className="h-5 w-5 text-emerald-700" />
        <h2 className="font-display text-lg font-bold text-forest-deep">Organisations</h2>
        <span className="rounded-full bg-emerald-900/5 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-emerald-800/70">{orgs.length} total</span>
      </div>
      <p className="mt-1 text-sm text-forest-deep/60">Client organisations and their accounts for the EPR Control Portal. You create the client admin and hand them the password.</p>

      <form onSubmit={createOrg} className="mt-5 rounded-2xl border-2 border-dashed border-emerald-950/15 bg-white/60 p-4" data-testid="org-create-form">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <input required placeholder="Legal name" value={orgDraft.legal_name} onChange={(e) => setOrgDraft((d) => ({ ...d, legal_name: e.target.value }))} className={inputCls} data-testid="org-input-legal-name" />
          <input placeholder="Trade name (optional)" value={orgDraft.trade_name} onChange={(e) => setOrgDraft((d) => ({ ...d, trade_name: e.target.value }))} className={inputCls} data-testid="org-input-trade-name" />
          <input required placeholder="Contact name" value={orgDraft.contact_name} onChange={(e) => setOrgDraft((d) => ({ ...d, contact_name: e.target.value }))} className={inputCls} data-testid="org-input-contact-name" />
          <input required type="email" placeholder="Contact email" value={orgDraft.contact_email} onChange={(e) => setOrgDraft((d) => ({ ...d, contact_email: e.target.value }))} className={inputCls} data-testid="org-input-contact-email" />
          <input placeholder="Contact phone" value={orgDraft.contact_phone} onChange={(e) => setOrgDraft((d) => ({ ...d, contact_phone: e.target.value }))} className={inputCls} data-testid="org-input-contact-phone" />
          <div className="flex gap-2">
            <select value={orgDraft.plan_tier} onChange={(e) => setOrgDraft((d) => ({ ...d, plan_tier: e.target.value }))} className={inputCls} data-testid="org-input-plan">
              <option value="free">Free</option>
              <option value="pro">Pro</option>
            </select>
            <input required type="number" min={1} placeholder="Seats" value={orgDraft.seat_limit} onChange={(e) => setOrgDraft((d) => ({ ...d, seat_limit: e.target.value }))} className={`${inputCls} w-24`} data-testid="org-input-seat-limit" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-forest-primary px-5 py-2.5 font-display text-sm font-bold text-white hover:bg-forest-rich disabled:opacity-60" data-testid="org-create-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create organisation
        </button>
      </form>

      {passwordOnce && (
        <div className="mt-4 rounded-2xl border-2 border-amber-400/60 bg-amber-50 p-4" data-testid="org-password-once">
          <p className="text-sm font-bold text-amber-900">Password for {passwordOnce.forName}, shown once only:</p>
          <p className="mt-1 select-all rounded-lg bg-white px-3 py-2 font-mono text-sm font-bold text-forest-deep" data-testid="org-password-once-value">{passwordOnce.password}</p>
          <p className="mt-2 text-xs text-amber-800">Copy it now and pass it to the client. It cannot be retrieved later. If lost, set a new one.</p>
        </div>
      )}

      {upgrades.filter((u) => u.status === "open").length > 0 && (
        <div className="mt-6 rounded-2xl border border-amber-400/50 bg-amber-50 p-4" data-testid="upgrade-requests">
          <h3 className="font-display text-sm font-bold text-amber-900">Open seat upgrade requests</h3>
          <div className="mt-2 space-y-2">
            {upgrades.filter((u) => u.status === "open").map((u) => (
              <div key={u.id} className="rounded-xl bg-white px-4 py-3 text-sm" data-testid={`upgrade-row-${u.id}`}>
                <span className="font-bold text-forest-deep">{u.org_name}</span>
                <span className="text-forest-deep/70"> asks for {u.requested_seats} seats (current {u.current_seat_limit}){u.note ? ` · ${u.note}` : ""}</span>
                <div className="mt-1 text-xs text-forest-deep/50">Raise the seat limit on their organisation card below, then mark it done in your own notes.</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {orgs.map((o) => {
          const users = orgUsers[o.id] || [];
          const hasAdmin = users.some((u) => u.account_type === "client_admin");
          return (
            <div key={o.id} className="rounded-2xl border border-emerald-950/10 bg-white shadow-sm" data-testid={`org-row-${o.id}`}>
              <button onClick={() => toggleExpand(o.id)} className="flex w-full flex-wrap items-center gap-3 px-5 py-4 text-left" data-testid={`org-toggle-${o.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-base font-bold text-forest-deep">{o.legal_name}</span>
                    <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${o.plan_tier === "pro" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>{o.plan_tier}</span>
                    <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${o.status === "active" ? "bg-emerald-500/15 text-emerald-700" : "bg-red-500/10 text-red-700"}`}>{o.status}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-forest-deep/55">{o.primary_contact?.name} · {o.primary_contact?.email} · seats {o.seats_used}/{o.seat_limit}</div>
                </div>
                <ChevronDown className={`h-5 w-5 text-emerald-800/60 transition-transform ${expanded === o.id ? "rotate-180" : ""}`} />
              </button>

              {expanded === o.id && (
                <div className="border-t border-emerald-950/10 px-5 py-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm font-semibold text-forest-deep/75">
                      Plan
                      <select value={o.plan_tier} onChange={(e) => patchOrg(o.id, { plan_tier: e.target.value })} className={inputCls} data-testid={`org-plan-${o.id}`}>
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-sm font-semibold text-forest-deep/75">
                      Seats
                      <input type="number" min={1} defaultValue={o.seat_limit} onBlur={(e) => Number(e.target.value) !== o.seat_limit && patchOrg(o.id, { seat_limit: Number(e.target.value) })} className={`${inputCls} w-20`} data-testid={`org-seats-${o.id}`} />
                    </label>
                    <button onClick={() => patchOrg(o.id, { status: o.status === "active" ? "suspended" : "active" })} className="rounded-full border border-emerald-950/15 px-4 py-2 text-sm font-semibold text-forest-deep hover:bg-emerald-900/5" data-testid={`org-status-${o.id}`}>
                      {o.status === "active" ? "Suspend" : "Reactivate"}
                    </button>
                  </div>

                  <div className="mt-4 space-y-2">
                    {users.map((u) => (
                      <div key={u.id} className="rounded-xl bg-parchment-base px-4 py-3" data-testid={`org-user-${u.id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-bold text-forest-deep">{u.name}</span>
                          <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-800/60">{u.account_type === "client_admin" ? "Client admin" : "Member"}</span>
                          <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-bold uppercase ${u.status === "active" ? "bg-emerald-500/15 text-emerald-700" : "bg-red-500/10 text-red-700"}`}>{u.status}</span>
                          <span className="text-xs text-forest-deep/55">{u.email}</span>
                          <span className="ml-auto flex items-center gap-1.5">
                            <button onClick={() => { setSetPwFor(setPwFor === u.id ? null : u.id); setSetPwValue(""); }} aria-label="Set password" className="rounded-full border border-emerald-950/15 p-1.5 text-forest-deep hover:bg-emerald-900/5" data-testid={`org-setpw-${u.id}`}>
                              <KeyRound className="h-3.5 w-3.5" />
                            </button>
                            {u.status === "active" ? (
                              <button onClick={() => setStatus(u.id, "disable", o.id)} aria-label="Disable" className="rounded-full border border-red-200 p-1.5 text-red-600 hover:bg-red-50" data-testid={`org-disable-${u.id}`}>
                                <UserX className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <button onClick={() => setStatus(u.id, "enable", o.id)} aria-label="Enable" className="rounded-full border border-emerald-600/30 p-1.5 text-emerald-700 hover:bg-emerald-50" data-testid={`org-enable-${u.id}`}>
                                <UserCheck className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </span>
                        </div>
                        {setPwFor === u.id && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <input type="text" minLength={10} placeholder="New password, minimum 10 characters" value={setPwValue} onChange={(e) => setSetPwValue(e.target.value)} className={`${inputCls} max-w-xs`} data-testid={`org-setpw-input-${u.id}`} />
                            <button onClick={() => savePassword(u.id)} className="rounded-full bg-forest-primary px-4 py-1.5 text-sm font-bold text-white hover:bg-forest-rich" data-testid={`org-setpw-save-${u.id}`}>Set</button>
                          </div>
                        )}
                      </div>
                    ))}
                    {users.length === 0 && <p className="text-sm text-forest-deep/50">No accounts yet.</p>}
                  </div>

                  {!hasAdmin && (
                    <div className="mt-4 rounded-xl border-2 border-dashed border-emerald-950/15 p-4">
                      {creatingAdminFor === o.id ? (
                        <div data-testid={`org-admin-form-${o.id}`}>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            <input placeholder="Full name" value={adminDraft.name} onChange={(e) => setAdminDraft((d) => ({ ...d, name: e.target.value }))} className={inputCls} data-testid="org-admin-name" />
                            <input type="email" placeholder="Work email" value={adminDraft.email} onChange={(e) => setAdminDraft((d) => ({ ...d, email: e.target.value }))} className={inputCls} data-testid="org-admin-email" />
                            <input placeholder="Designation" value={adminDraft.designation} onChange={(e) => setAdminDraft((d) => ({ ...d, designation: e.target.value }))} className={inputCls} data-testid="org-admin-designation" />
                            <input placeholder="Phone (optional)" value={adminDraft.phone} onChange={(e) => setAdminDraft((d) => ({ ...d, phone: e.target.value }))} className={inputCls} data-testid="org-admin-phone" />
                            <input type="text" minLength={10} placeholder="Password, minimum 10 characters" value={adminDraft.password} onChange={(e) => setAdminDraft((d) => ({ ...d, password: e.target.value }))} className={inputCls} data-testid="org-admin-password" />
                          </div>
                          <div className="mt-3 flex gap-2">
                            <button onClick={() => createClientAdmin(o.id)} className="rounded-full bg-forest-primary px-5 py-2 font-display text-sm font-bold text-white hover:bg-forest-rich" data-testid="org-admin-submit">Create client admin</button>
                            <button onClick={() => setCreatingAdminFor(null)} className="rounded-full border border-emerald-950/20 px-4 py-2 text-sm font-semibold text-forest-deep hover:bg-emerald-900/5">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => setCreatingAdminFor(o.id)} className="inline-flex items-center gap-1.5 text-sm font-bold text-forest-primary hover:text-forest-rich" data-testid={`org-create-admin-${o.id}`}>
                          <UserPlus className="h-4 w-4" /> Create client admin account
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
